import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import { Organization, User } from '../../../contexts/AuthContext';

interface MenuItem {
  title: string;
  icon: string;
  path: string;
  separator?: boolean;
  children?: {
    title: string;
    path: string;
    icon: string;
  }[];
}

interface MobileDrawerProps {
  show: boolean;
  onHide: () => void;
  menuItems: MenuItem[];
  user: User | null;
  organizations: Organization[];
  currentOrganization: Organization | null;
  onOrgChange: (org: Organization) => void;
  onLogout: () => void;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  show,
  onHide,
  menuItems,
  user,
  organizations,
  currentOrganization,
  onOrgChange,
  onLogout,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOrgExpanded, setIsOrgExpanded] = useState(false);

  const isActive = (path: string) => {
    const exactMatchPaths = ['/servers', '/events'];
    if (exactMatchPaths.includes(path)) {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    onHide();
  };

  const handleOrgSelect = (org: Organization) => {
    onOrgChange(org);
    setIsOrgExpanded(false);
  };

  const handleLogout = () => {
    onHide();
    onLogout();
  };

  return (
    <Offcanvas show={show} onHide={onHide} placement="start" className="mobile-drawer">
      <Offcanvas.Header closeButton>
        <div className="drawer-logo">
          <div
            className="drawer-logo-icon"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #7C3AED)' }}
          >
            <span style={{ color: '#fff', fontSize: 22, fontWeight: 700, lineHeight: 1 }}>C</span>
          </div>
          <span className="drawer-logo-text">CloudAyn</span>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body>
        {/* Organization Selector */}
        <div className="drawer-org-selector">
          <button
            className="btn-drawer-org"
            onClick={() => setIsOrgExpanded(!isOrgExpanded)}
          >
            <div className="drawer-org-info">
              <i className="bi bi-building"></i>
              <span>{currentOrganization?.name || 'Select Organization'}</span>
            </div>
            <i className={`bi ${isOrgExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </button>

          {isOrgExpanded && (
            <div className="mt-2">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  className={`mobile-dropdown-item ${currentOrganization?.id === org.id ? 'active' : ''}`}
                  onClick={() => handleOrgSelect(org)}
                  style={{ borderRadius: '8px', marginBottom: '4px' }}
                >
                  <i className="bi bi-building"></i>
                  <div>
                    <div className="fw-medium">{org.name}</div>
                    <small className="text-muted text-capitalize">{org.role}</small>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="drawer-nav">
          {menuItems.map((section, index) => (
            <div key={index} className="drawer-nav-section">
              {section.separator && <div className="drawer-nav-separator" />}
              <div className="drawer-nav-section-title">
                <i className={`bi ${section.icon}`}></i>
                <span>{section.title}</span>
              </div>
              {section.children?.map((item, childIndex) => (
                <Link
                  key={childIndex}
                  to={item.path}
                  className={`drawer-nav-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => onHide()}
                >
                  <i className={`bi ${item.icon}`}></i>
                  <span>{item.title}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="drawer-user-section">
          <div className="drawer-user-info">
            <div className="drawer-user-avatar">
              {user?.name.charAt(0).toUpperCase()}
            </div>
            <div className="drawer-user-details">
              <div className="drawer-user-name">{user?.name}</div>
              <div className="drawer-user-email">{user?.email}</div>
            </div>
          </div>
          <button className="btn-drawer-logout" onClick={handleLogout}>
            <i className="bi bi-box-arrow-right"></i>
            <span>Logout</span>
          </button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default MobileDrawer;
